import React, { FunctionComponent, Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { MdSearch, MdExitToApp, MdHome, MdInsertChart } from 'react-icons/md';
import { Logo, Import, Verified } from '@components/icons';
import { Routes } from '@components/routing';
import { AppEnums } from '@core/enums';
import { appActionCreators } from '@redux/app';
import { States, Models } from '@core/types';
import { usePassportContext } from '@tti/passport';
import onePlus from '@img/oneplus.png';
import onePlusHP from '@img/oneplus-hp.png';
import maxPower from '@img/max-power.png';
import _72v from '@img/72v.png';
import lithiumUSB from '@img/lithium-usb.png';
import link from '@img/link.png';

import { PassportEnums } from '@tti/passport';
import { push } from 'connected-react-router';

interface IProps {
  app: States.IAppState;
  product: States.IProductState;
  project: States.IProjectState;
  admin: States.IAdminState;
  setNavigation: (navigation: string) => void;
  redirect: (path: string, state?: {}) => void;
}

const Navigation: FunctionComponent<IProps> = ({ app, admin, product, project, setNavigation, redirect }) => {
  const { passportContext, getClaims, logOut } = usePassportContext();
  const approvalClaim = getClaims(PassportEnums.ClaimType.Role, passportContext.claims).find(x =>
    x.value.startsWith(PassportEnums.RoleType.SentToPrintApprover),
  );
  const reportingClaim = getClaims(PassportEnums.ClaimType.Role, passportContext.claims).find(x =>
    x.value.startsWith(PassportEnums.RoleType.ReportingAccess),
  );

  const closeNavigation = () => {
    if (app.activeNavigation !== AppEnums.Navigation.None) {
      setNavigation(AppEnums.Navigation.None);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Escape
    if (e.keyCode === 27 && app.activeNavigation !== AppEnums.Navigation.None) {
      closeNavigation();
    }
  };

  useEffect(() => {
    window.addEventListener('click', closeNavigation);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', closeNavigation);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!app) {
    return null;
  }

  const handleSetNavigation = (e: React.MouseEvent, navigation: string) => {
    if (app.activeNavigation === navigation) {
      setNavigation(AppEnums.Navigation.None);
      return;
    }

    setNavigation(navigation);
  };

  const getItemClasses = (navigationID: AppEnums.Navigation) => {
    let className = 'navigation__text';

    if (app.activeNavigation === navigationID) {
      className += ' active';
    }

    return className;
  };

  function getTab(navigationID: AppEnums.Navigation, category: Models.Category) {
   
     switch (navigationID) {
        case AppEnums.Navigation.OnePlus:
          return  <img alt="One Plus" src={onePlus} />;
          
        case AppEnums.Navigation.OnePlusHP:
          return  <img alt="One Plus HP" src={onePlusHP} />;
          
        case AppEnums.Navigation.MaxPower:
          return <img alt="Max Power" src={maxPower} />;
           
        case AppEnums.Navigation._72v:
          return  <img alt="72v" src={_72v} />;
            
        case AppEnums.Navigation.LithiumUSB:
          return  <img alt="Lithium USB" src={lithiumUSB} />;
          
        case AppEnums.Navigation.Link:
          return <img alt="Link" src={link} />;
           
        default: 
        return  <Fragment>{category?.categoryName}</Fragment>;
            
    }
}

  const renderCategory = (category: Models.Category | null, navigationID: AppEnums.Navigation) => {
    if (!category || category === null) {
      return null;
    }

    return (
      <div className={getItemClasses(navigationID)} onClick={e => handleSetNavigation(e, navigationID)}>
        {getTab(navigationID, category)}
      </div>
    );
  };

  return (
    <nav className="navigation" onClick={e => e.stopPropagation()}>
      <Link to={Routes.home.path}>
        <div className="navigation-logo">
          <Logo />
        </div>
      </Link>
      <div className="navigation__content">
        {/* Show project related icons when we have a project active */}
        {project.currentProject !== null && (
          <Fragment>
            {renderCategory(product.powerTools, AppEnums.Navigation.PowerTools)}
            {renderCategory(product.gardenTools, AppEnums.Navigation.GardenTools)}
            {renderCategory(product.batteriesChargers, AppEnums.Navigation.BatteriesChargers)}
            {renderCategory(product.accessories, AppEnums.Navigation.Accessories)}
            {renderCategory(product.handTools, AppEnums.Navigation.HandTools)}
            {renderCategory(product.onePlus, AppEnums.Navigation.OnePlus)}
            {renderCategory(product.onePlusHP, AppEnums.Navigation.OnePlusHP)}
            {renderCategory(product.maxPower, AppEnums.Navigation.MaxPower)}
            {renderCategory(product._72v, AppEnums.Navigation._72v)}
            {renderCategory(product.lithiumUSB, AppEnums.Navigation.LithiumUSB)}
            {renderCategory(product.link, AppEnums.Navigation.Link)}
          </Fragment>
        )}
      </div>
      {reportingClaim && (
        <div className="navigation__content">
          <div
            className={`navigation__icon ${window.location.pathname === Routes.reports.path ? 'active' : ''}`}
            onClick={() => redirect(Routes.reports.path)}
          >
            <MdInsertChart />
          </div>
        </div>
      )}
      <div className="navigation__content navigation__content--bottom">
        <div
          className={getItemClasses(AppEnums.Navigation.Import)}
          onClick={e => handleSetNavigation(e, AppEnums.Navigation.Import)}
        >
          <Import className="navigation__text--icon" />
          Import
        </div>
        <div
          className={getItemClasses(AppEnums.Navigation.Search)}
          onClick={e => handleSetNavigation(e, AppEnums.Navigation.Search)}
        >
          <MdSearch className="navigation__text--icon" />
          Search
        </div>
        <div className="navigation__icon" onClick={logOut}>
          <MdExitToApp />
        </div>
      </div>
    </nav>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  app: state.app,
  product: state.product,
  project: state.project,
  admin: state.admin,
});

const mapDispatchToProps = {
  setNavigation: (navigation: string) => appActionCreators.setActiveNav(navigation),
  redirect: (path: string, state?: {}) => push(path, state),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Navigation);
